import { useEffect, useState } from 'preact/hooks';
import { services } from '../app';
import { bortleFromSqm } from '../astro/lightpollution';
import { KIND_LABEL, Plan, SiteResult, coversPlace, directionsLink, planNight } from '../live/darksites';
import { crescentDay } from '../live/saudi';
import { measuredSqm } from '../live/skycheck';
import { loadJSON, panel, place, previewFrom, previewPlace, saveJSON, setSimTime, settings, simNow, timeVersion } from '../state';
import { Segmented, Sheet, Stat } from './chrome';
import { compassPoint, dayKey, fmtDate, fmtNum, fmtTime } from './format';
import { addDays } from '../lib/localtime';

/** Zenith brightness for each Bortle class, as the renderer uses. */
const BORTLE_SQM = [22.0, 21.9, 21.6, 21.1, 20.4, 19.5, 18.9, 18.4, 17.8];
import { Icon } from './icons';

const BORTLE_WORDS = ['excellent dark site', 'truly dark', 'rural', 'rural to suburban', 'suburban', 'bright suburban', 'suburban to urban', 'city', 'inner city'];

function HourStrip({ r, plan }: { r: { hours: SiteResult['hours'] }; plan: Plan }) {
  const max = Math.max(1, ...plan.results.map((x) => x.best.stars), plan.home.best?.stars ?? 0);
  return (
    <div class="hour-strip" aria-hidden="true">
      {r.hours.map((h) => (
        <div key={h.t} class="hour-cell" title={`${fmtTime(new Date(h.t))}: ${!h.ok ? 'before you could get there' : h.cloud == null ? 'no forecast' : `${Math.round(h.cloud)}% cloud`}`}>
          <div class="hour-bar" style={{ height: `${Math.max(6, (h.stars / max) * 100)}%`, opacity: !h.ok ? 0.12 : h.cloud == null ? 0.5 : 0.35 + 0.65 * (1 - h.cloud / 100) }} />
        </div>
      ))}
    </div>
  );
}

function ResultCard({ r, plan }: { r: SiteResult; plan: Plan }) {
  const s = r.site;
  const directions = directionsLink(s);
  const preview = () => {
    previewPlace({ lat: s.lat, lon: s.lon, height: 100, name: s.name, timeZone: place.peek().timeZone }, s.name);
    // Show the sky there at its best hour tonight.
    setSimTime(new Date(r.best.t), 1);
    panel.value = null;
  };
  const gain = (plan.home.best?.stars ?? 0) > 0 ? r.best.stars / plan.home.best!.stars : null;
  return (
    <div class={`site-card ${plan.bestBet === s.id ? 'top' : ''}`}>
      <div class="site-head">
        <div>
          <div class="site-name">{s.name}</div>
          <div class="site-sub">
            {KIND_LABEL[s.kind]}
            {s.within ? ` · in ${s.within}` : ''}
          </div>
        </div>
        <div class="site-drive tnum">
          <strong>{fmtNum(r.km, 0)} km</strong>
          <span>{compassPoint(r.bearing)}, in a straight line</span>
        </div>
      </div>
      <div class="stats three compact">
        <Stat label="Darkness" value={`Bortle ${bortleFromSqm(s.sqm)}`} sub={`${fmtNum(s.sqm, 1)} mag/arcsec² · ${BORTLE_WORDS[bortleFromSqm(s.sqm) - 1]}`} />
        <Stat label="Clear" value={r.clear == null ? 'unknown' : `${r.clear}%`} sub={r.clear == null ? 'no forecast' : 'of the dark hours'} />
        <Stat label="Stars" value={`~${r.best.stars.toLocaleString('en-GB')}`} sub={gain && gain >= 1.2 ? `${gain >= 10 ? Math.round(gain) : gain.toFixed(1)}× home, best ${fmtTime(new Date(r.best.t))}` : `best ${fmtTime(new Date(r.best.t))}`} />
      </div>
      <HourStrip r={r} plan={plan} />
      {s.cliff && <div class="note warn">Near a cliff edge: keep well back from it in the dark.</div>}
      <div class="site-actions">
        <a class="btn small" href={directions.href} target="_blank" rel="noopener noreferrer" aria-label={`Directions to ${s.name} in ${directions.label}`}><Icon name="pin" size={14} /> Directions</a>
        <button class="btn-ghost small" onClick={preview} aria-label={`Preview the sky from ${s.name}`}><Icon name="eye" size={14} /> Preview sky</button>
      </div>
      <div class="site-foot">
        {s.walk ? `Directions go to the car park; the spot is about ${s.walk} m from there in a straight line. ` : ''}
        Good from {fmtTime(new Date(r.from))} to {fmtTime(new Date(r.to))}. Night access not verified.
        {s.osm && (
          <>
            {' '}
            <a href={`https://www.openstreetmap.org/${s.osm}`} target="_blank" rel="noopener noreferrer" aria-label={`${s.name} on OpenStreetMap`}>Map</a>
          </>
        )}
      </div>
    </div>
  );
}

export function DarkSitesPanel() {
  const s = services.value!;
  const [maxMin, setMaxMin] = useState<number>(() => loadJSON<{ v: number }>('goMax', { v: 60 }).v);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // While previewing a suggested site, keep planning from home; a shared place is planned from itself.
  const home = previewFrom.value?.kind === 'site' ? previewFrom.value.home : place.value;
  void timeVersion.value;
  // One plan per night (it changes at midday), so time-lapse does not keep recalculating it.
  const night = Math.floor((simNow().getTime() - 12 * 3600e3) / 86400e3);
  useEffect(() => {
    let alive = true;
    setPlan(null);
    setErr(null);
    const set = settings.peek();
    // Home's sky as Naked eye has it: your darkness check there, else your own setting (the estimate from the map, or
    // the level you chose by hand).
    const homeSqm = measuredSqm(home) ?? (previewFrom.peek()?.kind === 'site' ? null : set.bortleAuto ? set.sqm : BORTLE_SQM[set.bortle - 1]);
    if (!coversPlace(home)) return;
    planNight(home, simNow(), maxMin, s.engine.stars.mag, homeSqm)
      .then((p) => alive && setPlan(p))
      .catch((e) => alive && setErr(String(e?.message ?? e)));
    return () => {
      alive = false;
    };
  }, [maxMin, home.lat, home.lon, night]);
  const choose = (v: number) => {
    setMaxMin(v);
    saveJSON('goMax', { v });
  };
  return (
    <Sheet onClose={() => (panel.value = null)} size="tall" label="Where to go tonight">
      <h2 class="sheet-title">{plan?.window && dayKey(plan.window.start) !== dayKey(Date.now()) && addDays(dayKey(plan.window.start), -1) !== dayKey(Date.now()) ? `Where to go on ${fmtDate(plan.window.start)}` : 'Where to go tonight'}</h2>
      {coversPlace(home) && (
        <div class="seg-row">
          <span class="row-label-inline">Drive up to</span>
          <Segmented label="Longest drive" value={maxMin} options={[[30, '30 min'], [60, '1 hr'], [90, '1½ hr'], [120, '2 hr']]} onChange={choose} />
        </div>
      )}
      {!coversPlace(home) && (
        <div class="verdict tone-none">
          <div class="verdict-title">Where to go covers the UK</div>
          <div class="verdict-text">Suggestions of dark places to visit are available across the UK (and Ireland). Everything else in Ahillah works wherever you are.</div>
        </div>
      )}
      {coversPlace(home) && !plan && !err && (
        <div class="working">
          <div class="spinner" />
          <p>Checking darkness, the Moon and the cloud forecast at public places near {home.name}…</p>
        </div>
      )}
      {err && <div class="note">Could not plan tonight: {err}</div>}
      {plan && coversPlace(home) && (
        <>
          <div class={`verdict tone-${plan.verdict.tone}`} role="status">
            <div class="verdict-title">{plan.verdict.title}</div>
            <div class="verdict-text">{plan.verdict.text}</div>
          </div>
          <div class="stats three">
            <Stat label="At home" value={`Bortle ${plan.home.bortle}`} sub={`${fmtNum(plan.home.sqm, 1)} mag/arcsec² · ${BORTLE_WORDS[plan.home.bortle - 1]}`} />
            {/* None counted where there is no proper darkness, or once the dark hours have passed. */}
            {plan.home.best && <Stat label="Home stars" value={`~${plan.home.best.stars.toLocaleString('en-GB')}`} sub={plan.home.clear != null ? `${plan.home.clear}% clear` : ''} />}
            {/* On the 29th, the 30th and the 1st, just "Crescent" (src/live/saudi.ts). */}
            {crescentDay(plan.window?.start ?? simNow().getTime(), home) ? (
              <Stat label="Moon" value="Crescent" sub="" />
            ) : (
              <Stat label="Moon" value={`${Math.round(plan.moon.lit * 100)}% lit`} sub={plan.moon.upHours ? `up ${plan.moon.upHours} of ${plan.home.hours.length} dark hours` : 'down while dark'} />
            )}
          </div>
          {plan.window && (
            <div class="row-sub pad-x">
              {plan.window.kind === 'astronomical' ? 'Fully dark' : 'Dark enough for stars'} from {fmtTime(new Date(plan.window.start))} to {fmtTime(new Date(plan.window.end))}.
              {!plan.forecast && ' The cloud forecast is unavailable, so clear skies are not checked.'}
            </div>
          )}
          {plan.results.map((r) => <ResultCard key={r.site.id} r={r} plan={plan} />)}
          {!plan.results.length && plan.verdict.tone !== 'none' && (
            <div class="note">No public places within about {plan.searchedKm} km are noticeably darker than home. Try a longer drive.</div>
          )}
          <div class="note safety">
            <strong>Before you go.</strong> These are suggestions only. Check the car park is open at night and the land is public. Stay on paths and open access land, never on farmland or
            gardens, and park only in proper car parks, never on verges. Tell someone where you are going, take a torch with a red setting and warm layers, and expect no phone signal.
          </div>
          <div class="fine">
            Darkness: World Atlas of the Artificial Night Sky Brightness (Falchi et al. 2016, CC BY-NC 4.0), from 2014–15 satellite data, so treat it as indicative. Places: © OpenStreetMap
            contributors (ODbL) and International Dark Sky Places (Spinner et al. 2024, CC BY 4.0). Cloud: Open-Meteo{plan.forecast ? '' : ' (unavailable)'}. Distances are in a
            straight line; the drive is longer.
          </div>
        </>
      )}
    </Sheet>
  );
}
